<template>
  <div class="app-navbar-item position-relative ms-1 ms-md-3" ref="notifDropdownRef">
    <!-- Bell Button -->
    <button
      type="button"
      class="btn btn-icon btn-custom btn-icon-muted btn-active-light btn-active-color-primary w-35px h-35px w-md-40px h-md-40px position-relative"
      @click.stop="toggleDropdown"
      title="Notificări"
      aria-label="Notificări"
    >
      <i class="fa-solid fa-bell fs-3" :class="unreadCount > 0 ? 'text-primary' : 'text-gray-500'"></i>
      
      <!-- Unread Badge -->
      <span
        v-if="unreadCount > 0"
        class="badge badge-circle badge-danger position-absolute top-0 end-0 translate-middle-y fs-9 fw-bolder shadow-xs"
        style="min-width: 18px; height: 18px; padding: 0 4px; line-height: 18px; font-size: 0.65rem;"
      >
        {{ unreadCount > 99 ? '99+' : unreadCount }}
      </span>
    </button>

    <!-- Dropdown Menu -->
    <div
      v-if="isOpen"
      class="menu menu-sub menu-sub-dropdown menu-column w-350px w-md-385px show p-0 shadow-lg rounded-3 bg-body border border-gray-200"
      style="position: absolute; top: 100%; right: 0; margin-top: 8px; z-index: 1055;"
      @click.stop
    >
      <!-- Header -->
      <div class="d-flex align-items-center justify-content-between p-3 px-4 border-bottom bg-light rounded-top-3">
        <div class="d-flex align-items-center gap-2">
          <i class="fa-solid fa-bell text-primary fs-5"></i>
          <h6 class="mb-0 fw-bold text-gray-900 fs-6">Notificări</h6>
          <span v-if="unreadCount > 0" class="badge badge-light-danger fw-bold fs-9 px-2 py-0.5">
            {{ unreadCount }} noi
          </span>
        </div>
        <div class="d-flex align-items-center gap-2">
          <button
            v-if="unreadCount > 0"
            type="button"
            class="btn btn-link text-primary p-0 fw-semibold fs-8"
            @click="markAllAsRead"
          >
            Toate citite
          </button>
          <button
            type="button"
            class="btn btn-sm btn-icon btn-light rounded-circle hover-scale"
            style="width: 26px; height: 26px;"
            @click="isOpen = false"
          >
            <i class="fa-solid fa-xmark fs-8"></i>
          </button>
        </div>
      </div>

      <!-- Filter Tabs (Toate / Necitite) -->
      <div class="d-flex px-4 pt-2 border-bottom bg-body">
        <button
          type="button"
          class="btn btn-sm btn-color-gray-600 btn-active-color-primary border-0 rounded-0 px-3 py-2 fw-semibold fs-8"
          :class="{ 'text-primary fw-bold border-bottom border-2 border-primary': currentTab === 'all' }"
          @click="currentTab = 'all'"
        >
          Toate ({{ notificari.length }})
        </button>
        <button
          type="button"
          class="btn btn-sm btn-color-gray-600 btn-active-color-primary border-0 rounded-0 px-3 py-2 fw-semibold fs-8"
          :class="{ 'text-primary fw-bold border-bottom border-2 border-primary': currentTab === 'unread' }"
          @click="currentTab = 'unread'"
        >
          Necitite ({{ unreadCount }})
        </button>
      </div>

      <!-- Notifications List -->
      <div class="overflow-y-auto py-1" style="max-height: 380px;">
        <div v-if="filteredNotificari.length > 0">
          <div
            v-for="item in filteredNotificari"
            :key="item.id"
            class="d-flex align-items-start gap-3 p-3 px-4 border-bottom border-gray-100 hover-bg-light transition-all"
            :class="{ 'bg-light-subtle': !item.read }"
            style="cursor: pointer;"
            @click="handleNotificationClick(item)"
          >
            <!-- Icon Symbol -->
            <div class="symbol symbol-35px symbol-circle flex-shrink-0 mt-1" style="width: 35px; height: 35px;">
              <div
                class="symbol-label fw-bold"
                :class="'bg-light-' + (item.tip || 'info') + ' text-' + (item.tip || 'info')"
                style="width: 35px; height: 35px; border-radius: 50%; display: flex; align-items: center; justify-content: center;"
              >
                <i :class="item.icon || 'fa-solid fa-bell'" class="fs-7"></i>
              </div>
            </div>

            <!-- Content -->
            <div class="flex-grow-1 min-w-0">
              <div class="d-flex align-items-center justify-content-between mb-0.5">
                <span class="fw-bold text-gray-900 fs-7 text-truncate me-2" :title="item.titlu">
                  {{ item.titlu }}
                </span>
                <span
                  v-if="!item.read"
                  class="badge badge-circle badge-primary"
                  style="width: 7px; height: 7px; flex-shrink: 0;"
                  title="Necitită"
                ></span>
              </div>
              <p class="text-gray-700 fs-8 mb-1 lh-sm">
                {{ item.mesaj }}
              </p>
              <div class="d-flex align-items-center justify-content-between text-muted fs-9">
                <span
                  v-if="item.firma_denumire"
                  class="text-truncate fw-semibold text-gray-600 me-2"
                  style="max-width: 170px;"
                >
                  <i class="fa-solid fa-building me-1 fs-9"></i>{{ item.firma_denumire }}
                </span>
                <span class="ms-auto text-nowrap">
                  <i class="fa-regular fa-clock me-0.5"></i>{{ item.time_ago }}
                </span>
              </div>
            </div>
          </div>
        </div>

        <!-- Empty State -->
        <div v-else class="text-center py-9 text-muted fs-8 px-4">
          <i class="fa-regular fa-bell-slash fs-2x text-gray-300 mb-2 d-block"></i>
          <span>{{ currentTab === 'unread' ? 'Nu ai nicio notificare necitită!' : 'Nu ai nicio notificare în istoric.' }}</span>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'AppHeaderNotifications',
  data() {
    return {
      isOpen: false,
      notificari: [],
      unreadCount: 0,
      currentTab: 'all', // 'all' | 'unread'
      pollInterval: null
    };
  },
  computed: {
    filteredNotificari() {
      if (this.currentTab === 'unread') {
        return this.notificari.filter(n => !n.read);
      }
      return this.notificari;
    }
  },
  mounted() {
    this.loadNotifications();

    // Polling discret la fiecare 60s
    this.pollInterval = setInterval(() => {
      this.loadNotifications(true);
    }, 60000);

    // Închide meniul la click în afara lui
    this._docClickHandler = (e) => {
      if (this.isOpen && this.$refs.notifDropdownRef && !this.$refs.notifDropdownRef.contains(e.target)) {
        this.isOpen = false;
      }
    };
    document.addEventListener('click', this._docClickHandler);

    // Ascultă evenimente globale de reîmprospătare
    if (typeof window !== 'undefined' && window.emitter) {
      this._refreshHandler = () => this.loadNotifications(true);
      window.emitter.on('refresh-notifications', this._refreshHandler);
    }
  },
  unmounted() {
    if (this.pollInterval) {
      clearInterval(this.pollInterval);
    }
    if (this._docClickHandler) {
      document.removeEventListener('click', this._docClickHandler);
    }
    if (typeof window !== 'undefined' && window.emitter && this._refreshHandler) {
      window.emitter.off('refresh-notifications', this._refreshHandler);
    }
  },
  methods: {
    toggleDropdown() {
      this.isOpen = !this.isOpen;
      if (this.isOpen) {
        this.loadNotifications();
      }
    },
    async loadNotifications(silent = false) {
      try {
        const res = await axios.get('/api/notificari');
        if (res.data && res.data.success) {
          this.notificari = res.data.notificari || [];
          this.unreadCount = res.data.unread_count || 0;
        }
      } catch (err) {
        if (!silent) {
          console.warn('Eroare la încărcare notificări:', err);
        }
      }
    },
    async markAsRead(item) {
      if (!item || item.read) return;
      try {
        item.read = true;
        this.unreadCount = Math.max(0, this.unreadCount - 1);
        await axios.post(`/api/notificari/${item.id}/citit`);
      } catch (err) {
        console.error('Eroare la marcare citit:', err);
      }
    },
    async markAllAsRead() {
      try {
        this.notificari.forEach(n => { n.read = true; });
        this.unreadCount = 0;
        await axios.post('/api/notificari/toate-citite');
      } catch (err) {
        console.error('Eroare la marcare toate citite:', err);
      }
    },
    async handleNotificationClick(item) {
      await this.markAsRead(item);
      this.isOpen = false;

      if (item.actiune_url) {
        if (this.$router && item.actiune_url.startsWith('/')) {
          this.$router.push(item.actiune_url).catch(() => {});
        } else {
          window.location.href = item.actiune_url;
        }
      }
    }
  }
};
</script>

<style scoped>
.hover-bg-light:hover {
  background-color: #f8f9fa;
}

[data-bs-theme="dark"] .hover-bg-light:hover {
  background-color: rgba(255, 255, 255, 0.05);
}

.hover-scale {
  transition: transform 0.15s ease;
}
.hover-scale:hover {
  transform: scale(1.1);
}
</style>
